import { ENV } from "../config/env";

// Type declarations for Google Tag Manager
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (command: string, ...args: any[]) => void;
  }
}

// Check if running in production
const isProduction = process.env.NODE_ENV === "production";

// Check if Google Analytics is available
const isGtagAvailable = (): boolean => {
  return typeof window !== "undefined" && typeof window.gtag === "function";
};

// Initialize Google Analytics
export const initAnalytics = (): void => {
  if (typeof window === "undefined") return;

  // Wait for gtag to be available
  const checkGtag = (attempts = 0) => {
    if (isGtagAvailable()) {
      if (isProduction) {
        console.log("Google Analytics initialized");
      } else {
        console.log("Google Analytics initialized (development mode)");
      }
      trackPageView(window.location.pathname);
    } else if (attempts < 5) {
      // Retry after a delay
      setTimeout(() => checkGtag(attempts + 1), 300);
    } else {
      console.error(
        "Failed to initialize Google Analytics: gtag not available",
      );
    }
  };

  checkGtag();
};

// Track page views
export const trackPageView = (
  url: string,
  title: string = document.title,
): void => {
  if (!isGtagAvailable()) {
    console.warn("Google Analytics not available for page view tracking");
    return;
  }

  try {
    window.gtag("event", "page_view", {
      page_title: title,
      page_location: window.location.href,
      page_path: url,
      send_to: ENV.GA_MEASUREMENT_ID,
    });
  } catch (error) {
    console.error("Error tracking page view:", error);
  }
};

// Track events
export const trackEvent = (
  action: string,
  category: string,
  label: string,
  value?: number,
  additionalParams: Record<string, any> = {},
): void => {
  if (!isGtagAvailable()) {
    if (isProduction) {
      console.warn("Google Analytics not available for event tracking");
    }
    return;
  }

  try {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
      value: value,
      send_to: ENV.GA_MEASUREMENT_ID,
      ...additionalParams,
      non_interaction: false,
    });
  } catch (error) {
    console.error("Error tracking event:", error);
  }
};

// Track exceptions
export const trackException = (
  description: string,
  fatal: boolean = false,
): void => {
  if (typeof window === "undefined" || !window.gtag) return;

  window.gtag("event", "exception", {
    description,
    fatal,
  });
};
